import { supabase } from "../lib/supabase";
const functionsUrl = import.meta.env.VITE_SUPABASE_FUNCTIONS_URL;
const supabaseAnonKey = import.meta.env.VITE_SUPABASE_ANON_KEY;

async function getAccessToken() {
  const {
    data: { session },
  } = await supabase.auth.getSession();
  return session?.access_token || null;
}

/**
 * Fetch Threads posts (scheduled/published/failed) with optional filters
 * @param {Object} filters - { status, accountId, startDate, endDate }
 * @returns {Promise<Array>}
 */
export async function fetchThreadsPosts(filters = {}) {
  try {
    const threadsQuery = (from, to) => {
      let query = supabase.from("threads_scheduled_posts").select("*");
      if (filters.profileId) query = query.eq("profile_id", filters.profileId);
      return query.order("scheduled_at", { ascending: false }).range(from, to);
    };
    const instagramQuery = (from, to) => {
      let query = supabase
        .from("instagram_scheduled_posts")
        .select("article_id,caption,image_url,scheduled_at,permalink")
        .eq("status", "published")
        .not("permalink", "is", null);
      if (filters.profileId) query = query.eq("profile_id", filters.profileId);
      return query.order("scheduled_at", { ascending: false }).range(from, to);
    };
    const [threadsFirst, threadsSecond, instagramFirst, instagramSecond] = await Promise.all([
      threadsQuery(0, 999),
      threadsQuery(1000, 1999),
      instagramQuery(0, 999),
      instagramQuery(1000, 1999),
    ]);
    const error = threadsFirst.error || threadsSecond.error;

    if (error) {
      console.error("[threadsPostsService] Error fetching posts:", error);
      throw error;
    }

    const instagramPosts = instagramFirst.error || instagramSecond.error
      ? []
      : [...(instagramFirst.data || []), ...(instagramSecond.data || [])];
    let filteredPosts = [...(threadsFirst.data || []), ...(threadsSecond.data || [])].map((post) => {
      let closest = null;
      let closestDistance = Number.POSITIVE_INFINITY;
      for (const instagramPost of instagramPosts) {
        const sameArticle = post.article_id && instagramPost.article_id === post.article_id;
        const sameContent = instagramPost.caption === post.caption && instagramPost.image_url === post.image_url;
        if (!sameArticle && !sameContent) continue;
        const distance = Math.abs(new Date(instagramPost.scheduled_at) - new Date(post.scheduled_at));
        if (distance < closestDistance) {
          closest = instagramPost;
          closestDistance = distance;
        }
      }
      return {
        ...post,
        instagram_permalink: closestDistance <= 15 * 60 * 1000 ? closest?.permalink || null : null,
      };
    });

    if (filters.status && filters.status !== "all") {
      filteredPosts = filteredPosts.filter((post) => post.status === filters.status);
    }

    if (filters.accountId) {
      filteredPosts = filteredPosts.filter((post) => post.account_id === filters.accountId);
    }

    if (filters.startDate) {
      filteredPosts = filteredPosts.filter(
        (post) => new Date(post.scheduled_at) >= new Date(filters.startDate)
      );
    }

    if (filters.endDate) {
      filteredPosts = filteredPosts.filter(
        (post) => new Date(post.scheduled_at) <= new Date(filters.endDate)
      );
    }

    return filteredPosts;
  } catch (err) {
    console.error("[threadsPostsService] Failed to fetch posts:", err);
    throw err;
  }
}

/**
 * Delete a Threads scheduled/logged post
 * @param {string} postId
 */
export async function deleteThreadsPost(postId) {
  const { error } = await supabase
    .from("threads_scheduled_posts")
    .delete()
    .eq("id", postId);

  if (error) {
    console.error("[threadsPostsService] Error deleting post:", error);
    throw error;
  }
}

/**
 * Fetch the user's connected Threads accounts
 * @returns {Promise<Array>}
 */
export async function fetchThreadsAccounts(profileId = null) {
  try {
    const token = await getAccessToken();
    if (!token || !functionsUrl) return [];

    const url = profileId
      ? `${functionsUrl}/threads-accounts?profileId=${encodeURIComponent(profileId)}`
      : `${functionsUrl}/threads-accounts`;
    const res = await fetch(url, {
      headers: { Authorization: `Bearer ${token}`, apikey: supabaseAnonKey },
    });
    if (!res.ok) return [];
    const json = await res.json();
    return json.accounts || [];
  } catch (err) {
    console.error("[threadsPostsService] Failed to fetch accounts:", err);
    return [];
  }
}
